import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';

/**
 * Displays comprehensive interview metadata - Monochrome black and white theme
 * @param {Object} metadata - Interview metadata from evaluation.interview_metadata
 */
const InterviewMetadataPanel = ({ metadata }) => {
  if (!metadata) return null;

  const { experience_assessment, confidence_score, specific_evidence_count } = metadata;

  const cardStyles = {
    p: 2.5,
    backgroundColor: 'rgba(12, 14, 22, 0.82)',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 14px 36px rgba(0, 0, 0, 0.35)',
    backdropFilter: 'blur(14px)',
    transition: 'all 0.25s ease',
    '&:hover': {
      boxShadow: '0 18px 42px rgba(0, 0, 0, 0.45)',
      borderColor: 'rgba(255, 255, 255, 0.18)',
    },
  };

  const headingStyles = {
    color: 'rgba(255,255,255,0.95)',
    fontWeight: 600,
    mb: 2.5,
    fontSize: '1.05rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: { xs: 2.5, md: 3 },
        mb: 2.5,
      }}
    >
      {/* Experience Assessment Card */}
      <Box sx={{ ...cardStyles }}>
        <Typography variant="h6" sx={{ ...headingStyles }}>
          Interview Intelligence
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Years of Experience */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                Years of Experience
              </Typography>
              <Typography variant="body1" sx={{ color: 'white', fontWeight: 700 }}>
                {experience_assessment?.years_experience || 0} years
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min((experience_assessment?.years_experience || 0) * 10, 100)}
              sx={{
                height: 5,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 3,
                },
              }}
            />
          </Box>

          {/* Seniority Match */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '14px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500, fontSize: '0.85rem' }}>
              Seniority Level
            </Typography>
            <Box
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 700,
                  textTransform: 'capitalize',
                  fontSize: '0.85rem',
                }}
              >
                {experience_assessment?.seniority_match || 'Unknown'}
              </Typography>
            </Box>
          </Box>

          {/* Project Complexity */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '14px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500, fontSize: '0.85rem' }}>
              Project Complexity
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700, textTransform: 'capitalize', fontSize: '0.85rem' }}
            >
              {experience_assessment?.project_complexity || 'Unknown'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Confidence & Evidence Card */}
      <Box sx={{ ...cardStyles }}>
        <Typography variant="h6" sx={{ ...headingStyles }}>
          Confidence Meter
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Confidence Score */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500, fontSize: '0.85rem' }}>
                Confidence Score
              </Typography>
              <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 700, fontSize: '1.2rem' }}>
                {confidence_score || 0}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={confidence_score || 0}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 3,
                },
              }}
            />
          </Box>

          {/* Evidence Strength */}
          <Box
            sx={{
              p: 2.5,
              backgroundColor: 'rgba(255,255,255,0.03)',
              borderRadius: '14px',
              border: '1px solid rgba(255,255,255,0.06)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h4" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 700, mb: 0.5, fontSize: '1.8rem' }}>
              {specific_evidence_count || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
              Evidence Points
            </Typography>
          </Box>

          {/* Confidence Level Text */}
          <Box
            sx={{
              p: 2,
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '14px',
              border: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', lineHeight: 1.5 }}>
              {confidence_score >= 70
                ? 'High confidence in evaluation accuracy'
                : confidence_score >= 40
                ? 'Moderate confidence - review recommended'
                : 'Low confidence - requires manual review'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InterviewMetadataPanel;
